import React, { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { useNavigate, useLocation } from 'react-router-dom'
import { Paper } from '@mui/material'

import { TableUrlPagination } from '../../../../ui'
import { JCA_TABLE_COLUMNS_GUILD, JCA_TABLE_COLUMNS } from '../../constants'
import { jcaTableStyles } from './jca-table.styles'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const JCATable = () => {
  const { formatMessage,formatDate } = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const {
    data,
    search,
    pagination: { total_registros: totalEntries },
    isLoading,
  } = useSelector((state) => state.jca)
  const combos = useSelector((state) => state.combos)
  const global = useSelector((state) => state.global)
  const isGuild = !!global.gremio

  const formattedColumns = useMemo(
    () => {
      const columns = isGuild ? JCA_TABLE_COLUMNS_GUILD : JCA_TABLE_COLUMNS
      return columns.map((column) => {
        // Base column configuration for Material React Table
        const baseColumn = {
          accessorKey: column.field,
          id: column.field,
          header: formatMessage({ id: `pages.jcaList.table.columns.${column.field}.title` }),
          size: column.width,
          minSize: column.minWidth || column.width,
          maxSize: column.maxWidth,
          enableSorting: column.sorting !== false,
          muiTableHeadCellProps: {
            align: column.align || 'left',
          },
          muiTableBodyCellProps: {
            align: column.align || 'left',
            sx: {
              minWidth: column.minWidth || column.width,
            },
          },
        }

        // Add lookup functionality if combo data exists
        if (column.hasOwnProperty('comboId') && combos[column.comboId] && combos[column.comboId].data) {
          const lookup = getLookup(combos[column.comboId].data)
          baseColumn.Cell = ({ cell }) => {
            const value = cell.getValue()
            return lookup[value] || value || <span>--</span>
          }
        } else {
          // Default cell renderer with empty value handling
          baseColumn.Cell = ({ cell }) => {
            const value = cell.getValue()
            return value || <span>--</span>
          }
        }

        return baseColumn
      })
    },
    [combos, isGuild, formatMessage] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const processedData = useMemo(
    () =>
      data.map((item) => {
        return {
          ...item,
          fecha_jca: formatDate(new Date(item.fecha_jca)),
          fecha_creacion_jca: formatDate(new Date(item.fecha_creacion_jca)),
          tableData: {
            ...item.tableData,
          },
        }
      }),
    [data]
  )

  const tableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      tableLayout: 'fixed',
      draggable: false,
    }),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleRowClick = useCallback((_, row) => {
    const props = {
      pathname: `/jca/${row.id}`,
      state: { prevSearch: location.search },
    }
    return navigate(props)
  }, [navigate, location.search])

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={isLoading}
      onRowClick={handleRowClick}
    />
  )
}
